import React, { useEffect, useState } from 'react';
import apiService from '../services/apiService';
import {
  FormRow,
  FormWrapper,
  Label,
  SubmitButton,
  CancelButton,
} from '../styles/ComponentStyles';
import {
  StatusSelect,
  BillDateInput,
  BillNumberInput,
  InstallmentSection,
  InstallmentCheckboxWrapper,
  HelperText,
} from '../styles/BillsFormStyles';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';

// Fallback lists mirror the backend enums; replaced by /get_options once loaded.
const DEFAULT_FREQUENCIES = ['One-time', 'Weekly', 'Monthly'];
const DEFAULT_STATUSES = ['Ongoing', 'Settled', 'Written-off'];

const inputStyle = {
  width: '100%',
  maxWidth: 400,
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
  color: '#e0e0e0',
};

const ReceivableFormModal = ({ editing, initialData, onSubmit, onClose }) => {
  const [frequencies, setFrequencies] = useState(DEFAULT_FREQUENCIES);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [formData, setFormData] = useState({
    debtor: '',
    principal: '',
    start_date: '',
    frequency: 'Monthly',
    number_of_installments: '',
    fixed_installment: true,
    has_interest: false,
    interest_rate: '',
    status: 'Ongoing',
    remarks: '',
  });

  useEffect(() => {
    let active = true;
    apiService
      .getReceivableOptions()
      .then((res) => {
        const data = res?.data || res;
        if (!active || !data) return;
        if (Array.isArray(data.frequencies) && data.frequencies.length) {
          setFrequencies(data.frequencies);
        }
        if (Array.isArray(data.statuses) && data.statuses.length) {
          setStatuses(data.statuses);
        }
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (editing && initialData) {
      setFormData({
        debtor: initialData.debtor || '',
        principal: initialData.principal ?? '',
        start_date: initialData.start_date ?? '',
        frequency: initialData.frequency || 'Monthly',
        number_of_installments: initialData.number_of_installments ?? '',
        fixed_installment:
          initialData.fixed_installment != null ? initialData.fixed_installment : true,
        has_interest: !!initialData.has_interest,
        interest_rate: initialData.interest_rate ?? '',
        status: initialData.status || 'Ongoing',
        remarks: initialData.remarks ?? '',
      });
    } else {
      setFormData({
        debtor: '',
        principal: '',
        start_date: '',
        frequency: 'Monthly',
        number_of_installments: '',
        fixed_installment: true,
        has_interest: false,
        interest_rate: '',
        status: 'Ongoing',
        remarks: '',
      });
    }
  }, [editing, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      debtor: formData.debtor,
      principal: formData.principal ? Number(formData.principal) : 0,
      start_date: formData.start_date || null,
      frequency: formData.frequency || 'Monthly',
      number_of_installments: formData.number_of_installments
        ? Number(formData.number_of_installments)
        : null,
      fixed_installment: !!formData.fixed_installment,
      has_interest: !!formData.has_interest,
      interest_rate:
        formData.has_interest && formData.interest_rate !== ''
          ? Number(formData.interest_rate)
          : null,
      remarks: formData.remarks || null,
    };
    // Status is only editable on an existing receivable.
    if (editing) {
      payload.status = formData.status || 'Ongoing';
    }
    onSubmit(payload);
    onClose();
  };

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{editing ? 'Edit Receivable' : 'Add Receivable'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>

        <FormWrapper onSubmit={handleSubmit}>
          <FormRow>
            <Label htmlFor="debtor">Debtor*</Label>
            <input
              id="debtor"
              name="debtor"
              type="text"
              value={formData.debtor ?? ''}
              onChange={handleChange}
              required
              placeholder="Who owes you"
              style={inputStyle}
            />
          </FormRow>

          <FormRow>
            <Label htmlFor="principal">Principal (amount lent)*</Label>
            <BillNumberInput
              id="principal"
              name="principal"
              min="0"
              value={formData.principal ?? ''}
              onChange={handleChange}
              required
              placeholder="e.g. 20000"
            />
          </FormRow>

          <FormRow>
            <Label htmlFor="start_date">Start Date (optional)</Label>
            <HelperText>When it was lent</HelperText>
            <BillDateInput
              id="start_date"
              name="start_date"
              value={formData.start_date ?? ''}
              onChange={handleChange}
            />
          </FormRow>

          <FormRow>
            <Label htmlFor="frequency">Frequency</Label>
            <StatusSelect
              id="frequency"
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
            >
              {frequencies.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </StatusSelect>
          </FormRow>

          {editing && (
            <FormRow>
              <Label htmlFor="status">Status</Label>
              <StatusSelect
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </StatusSelect>
            </FormRow>
          )}

          <InstallmentSection>
            <FormRow>
              <Label htmlFor="number_of_installments">
                Number of Installments (optional)
              </Label>
              <HelperText>Leave blank for an open-ended debt</HelperText>
              <BillNumberInput
                id="number_of_installments"
                name="number_of_installments"
                min="1"
                value={formData.number_of_installments ?? ''}
                onChange={handleChange}
                placeholder="e.g. 4, 12"
              />
            </FormRow>
            <FormRow>
              <InstallmentCheckboxWrapper>
                <input
                  type="checkbox"
                  name="fixed_installment"
                  checked={!!formData.fixed_installment}
                  onChange={handleCheckbox}
                />
                <span>Fixed installment amount</span>
              </InstallmentCheckboxWrapper>
              <HelperText>
                When on, each installment is a fixed derived amount. When off, payments
                are variable.
              </HelperText>
            </FormRow>
          </InstallmentSection>

          <InstallmentSection>
            <FormRow>
              <InstallmentCheckboxWrapper>
                <input
                  type="checkbox"
                  name="has_interest"
                  checked={!!formData.has_interest}
                  onChange={handleCheckbox}
                />
                <span>Charge interest</span>
              </InstallmentCheckboxWrapper>
            </FormRow>
            {formData.has_interest && (
              <FormRow>
                <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                <HelperText>Applied to the principal</HelperText>
                <BillNumberInput
                  id="interest_rate"
                  name="interest_rate"
                  min="0"
                  value={formData.interest_rate ?? ''}
                  onChange={handleChange}
                  placeholder="e.g. 5"
                />
              </FormRow>
            )}
          </InstallmentSection>

          <FormRow>
            <Label htmlFor="remarks">Remarks</Label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks ?? ''}
              onChange={handleChange}
              rows={3}
              style={inputStyle}
            />
          </FormRow>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <SubmitButton type="submit">{editing ? 'Save Changes' : 'Add'}</SubmitButton>
            <CancelButton type="button" onClick={onClose}>
              Cancel
            </CancelButton>
          </div>
        </FormWrapper>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default ReceivableFormModal;
